import _ from 'lodash';
import Glyphicons from '@remind/r101-glyphs';
import { GlyphName } from '@remind/r101-glyphs';
import getPropClasses from 'lib/getPropClasses';
import * as React from 'react';
import cx from 'classnames';
import GlobalAttributes from 'constants/GlobalAttributes';
import SupportedEventNames from 'constants/SupportedEventNames';

import './styles/Glyph.scss';

const ALLOWED_PROPS = _.union(GlobalAttributes, SupportedEventNames);

export type GlyphSize =
  | 'inherit'
  | 'xxxsmall'
  | 'xxsmall'
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | 'xxlarge'
  | 'xxxlarge';

export type GlyphColor =
  | 'inherit'
  | 'apple'
  | 'elmer'
  | 'pencil'
  | 'atlas'
  | 'mascot'
  | 'pennant'
  | 'graphite'
  | 'ballpoint'
  | 'percussion'
  | 'chalkboard'
  | 'ruler'
  | 'paper'
  | 'whiteout'
  | 'facebook'
  | 'pinterest'
  | 'pool'
  | 'twitter'
  | 'black';

type CoachingGlyphColor = 'koolaid';

type Props = {
  name: GlyphName;
  solo?: boolean;
  className?: string;
  // remaining used by getPropClasses
  /* eslint-disable react/no-unused-prop-types */
  size?: GlyphSize;
  color?: GlyphColor | CoachingGlyphColor;
};

/**
 * Glyph provides icons through a web font maintained in the r101-glyphs respository.
 *
 * Glyphicons use a web font and can be styled like text on a web page. You can customize the size
 * and color by simply inheriting the font size and text color of the container the `Glyph` is used
 * in or by specifying a size and/or color through props.
 *
 * _TODO: Use JavaScript to render this example._
 *
 * ```example
 * <div>
 *   <Glyph name="facebook" color="facebook" size="xxxlarge" />
 *   <Glyph name="twitter" color="twitter" size="xxxlarge" />
 *   <Glyph name="youtube" color="apple" size="xxxlarge" />
 * </div>
 * ```
 */

class Glyph extends React.Component<Props> {
  static propClasses = {
    size: {
      xxxsmall: 'Glyph-sizeXxxSmall',
      xxsmall: 'Glyph-sizeXxSmall',
      xsmall: 'Glyph-sizeXSmall',
      small: 'Glyph-sizeSmall',
      medium: 'Glyph-sizeMedium',
      large: 'Glyph-sizeLarge',
      xlarge: 'Glyph-sizeXLarge',
      xxlarge: 'Glyph-sizeXxLarge',
      xxxlarge: 'Glyph-sizeXxxLarge',
    },
    color: {
      apple: 'Glyph--color-apple',
      elmer: 'Glyph--color-elmer',
      pencil: 'Glyph--color-pencil',
      atlas: 'Glyph--color-atlas',
      mascot: 'Glyph--color-mascot',
      pennant: 'Glyph--color-pennant',
      graphite: 'Glyph--color-graphite',
      ballpoint: 'Glyph--color-ballpoint',
      percussion: 'Glyph--color-percussion',
      chalkboard: 'Glyph--color-chalkboard',
      paper: 'Glyph--color-paper',
      whiteout: 'Glyph--color-whiteout',
      facebook: 'Glyph--color-facebook',
      pinterest: 'Glyph--color-pinterest',
      twitter: 'Glyph--color-twitter',
      pool: 'Glyph--color-pool',
      koolaid: 'Glyph--color-koolaid',
    },
  };

  static defaultProps = {
    size: 'inherit',
    color: 'inherit',
  };

  render() {
    const { className, name } = this.props;
    const iconProps = _.pick(this.props, ALLOWED_PROPS);

    return (
      <i
        {...iconProps}
        className={cx(
          'Glyph',
          Glyphicons[name] || 'Glyph-unknown',
          { 'Glyph--solo': this.props.solo },
          getPropClasses(this.props, Glyph.propClasses),
          className
        )}
      />
    );
  }
}

export default Glyph;
